import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Select, SelectProps } from 'tea-component';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { SELECT_LIST_HEIGHT } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { AREAS, AREAS_NO_GLOBAL, AREAS_NAMES_MAPPING } from '../constants';
import './index.scss';

import type { IBaseProps, ITranslater } from '@/pages/intelligence/types/common';
import type { IArea } from '../types';

const getOptions = (areas: IArea[], translater: ITranslater) => {
  if (!Array.isArray(areas)) return [];
  return areas.map((area) => {
    const translateKey = AREAS_NAMES_MAPPING?.[area];
    const translateText = translater?.({ id: translateKey });
    return {
      value: area,
      text: translateText,
    };
  });
};

interface ISingleAreaSelectProps extends IBaseProps, SelectProps {
  isNoGlobal?: boolean;
  isShowButtonLabel?: boolean;
}

const SingleAreaSelect: React.FC<ISingleAreaSelectProps> = (props) => {
  const {
    style,
    className,
    overlayStyle,
    overlayClassName,
    disabled,
    isNoGlobal = true,
    isShowButtonLabel = true,
    value,
    onChange,
  } = props;

  const { locale, formatMessage } = useIntlExtend();

  const labelText = formatMessage({ id: 'dbi_area_region' });

  const options = useMemo(() => {
    const areas = isNoGlobal ? AREAS_NO_GLOBAL : AREAS;
    return getOptions(areas, formatMessage);
  }, [locale, isNoGlobal]);

  const button = useMemo(() => {
    const option = options?.find((item) => item?.value === value);
    return (
      <div className="dbi-select-button">
        {isShowButtonLabel && <div className="dbi-select-button-text">{labelText}</div>}
        <div className="dbi-select-flex">{option?.text}</div>
      </div>
    );
  }, [value, options, isShowButtonLabel, labelText]);

  const handleChange = (v: string, context: any) => {
    onChange?.(v, context);
  };

  return (
    <Select
      style={style}
      className={classnames('dbi-single-area-select search-item', className)}
      overlayStyle={overlayStyle}
      overlayClassName={classnames('dbi-single-area-select-overlay', overlayClassName)}
      appearance="button"
      listHeight={SELECT_LIST_HEIGHT}
      disabled={disabled}
      matchButtonWidth={true}
      button={button}
      options={options}
      value={value}
      onChange={handleChange}
    />
  );
};

export default SingleAreaSelect;
